import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  LayoutDashboard, 
  Search, 
  MousePointerClick, 
  Mail, 
  Users, 
  FileText, 
  LogOut, 
  Menu, 
  X,
  ChevronDown,
  UserCog,
  BarChart3,
  FilePlus,
  KeyRound
} from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changing, setChanging] = useState(false);
  const isAdmin = user?.role === "admin";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("As passwords não coincidem");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("A nova password deve ter pelo menos 6 caracteres");
      return;
    }
    
    setChanging(true);
    try {
      await axios.put(`${API_URL}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success("Password alterada com sucesso");
      setPasswordDialogOpen(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao alterar password");
    } finally {
      setChanging(false);
    }
  };

  const clientNavItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/seo", icon: Search, label: "SEO" },
    { to: "/ads", icon: MousePointerClick, label: "Google Ads" },
    { to: "/email", icon: Mail, label: "Email Marketing" },
  ];

  const annualReportItems = [
    { to: "/annual/seo", icon: Search, label: "SEO" },
    { to: "/annual/ads", icon: MousePointerClick, label: "Google Ads" },
    { to: "/annual/email", icon: Mail, label: "Email Marketing" },
  ];

  const adminNavItems = [
    { to: "/admin/clients", icon: Users, label: "Clientes" },
    { to: "/admin/view-reports", icon: BarChart3, label: "Consultar Relatórios" },
    { to: "/admin/reports", icon: FilePlus, label: "Inserir Relatórios" },
  ];

  const NavItem = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        `sidebar-link ${isActive ? "active" : ""}`
      }
      data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <Icon className="w-5 h-5" strokeWidth={1.5} />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-slate-100 rounded-lg"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <img 
          src="/images/logo.png" 
          alt="First Media Logo"
          className="h-10 w-auto object-contain"
        />
        <div className="w-10" />
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-40 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center px-6 py-4 border-b border-slate-100">
            <img 
              src="/images/logo.png" 
              alt="First Media Logo"
              className="h-14 w-auto object-contain"
            />
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Relatórios
            </p>
            {clientNavItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}

            <div className="my-4 border-t border-slate-200" />
            <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Relatórios Anuais
            </p>
            {annualReportItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}

            {isAdmin && (
              <>
                <div className="my-4 border-t border-slate-200" />
                <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Administração
                </p>
                {adminNavItems.map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </>
            )}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-slate-200 space-y-2">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-semibold">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </span>
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500">{isAdmin ? "Administrador" : "Cliente"}</p>
              </div>
            </div>
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => setPasswordDialogOpen(true)}
                className="w-full flex items-center justify-center gap-2 text-slate-600 hover:text-amber-600 hover:border-amber-200 hover:bg-amber-50"
                data-testid="change-password-btn"
              >
                <KeyRound className="w-4 h-4" />
                Alterar Password
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 text-slate-600 hover:text-red-600 hover:border-red-200 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Terminar sessão
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <KeyRound className="w-5 h-5 text-amber-600" />
              Alterar Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Password Atual</Label>
              <Input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword">Nova Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Nova Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setPasswordDialogOpen(false)} 
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleChangePassword}
                disabled={changing || !currentPassword || !newPassword || !confirmPassword}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-change-password-btn"
              >
                {changing ? "A alterar..." : "Alterar Password"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
